import { useOutletContext } from "react-router-dom";

import { DisclosureLink, EmptyState, Money, Pagination, SkeletonRows, Status } from "../../../../components/ui";
import { describeReview, historyState, isWithHr, periodFacts, periodName, periodStatus } from "./payrollFinance";

/** One period Finance has no action on: what it is, where it stands, its money, and the decisions behind it. */
function HistoryRow({ period }) {
  const status = periodStatus(period);
  const hasPayslips = Number(period.payslip_count || 0) > 0;
  const tone = status === "settled" ? "success" : "neutral";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 px-4 py-3 text-cell md:grid-cols-[minmax(0,1fr)_8.5rem_16rem]">
      <div className="min-w-0">
        <p className="text-body font-medium text-foreground">{periodName(period)}</p>
        <p className="text-meta text-secondary">{periodFacts(period)}</p>
      </div>
      <div className="text-right">
        {hasPayslips ? (
          <>
            <Money value={period.net_pay_cents} className="text-money text-foreground" />
            <p className="text-meta text-tertiary">net pay</p>
          </>
        ) : null}
      </div>
      <div className="col-span-full md:col-span-1">
        <Status tone={tone} icon={tone === "success"}>{historyState(period)}</Status>
      </div>
      {period.reviews?.length ? (
        <DisclosureLink
          className="col-span-full"
          label={`Finance decisions (${period.reviews.length})`}
        >
          <ul className="mt-1 space-y-1 border-l border-subtle pl-3 text-body-sm text-secondary">
            {period.reviews.map((review, index) => <li key={`${review.createdAt}-${index}`}>{describeReview(review)}</li>)}
          </ul>
        </DisclosureLink>
      ) : null}
    </li>
  );
}

function HistoryGroup({ title, description, periods, footer = null }) {
  if (!periods.length) return null;
  const id = `history-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <section aria-labelledby={id} className="space-y-2">
      <div>
        <h2 id={id} className="text-section-title text-foreground">{title}</h2>
        {description ? <p className="text-body-sm text-secondary">{description}</p> : null}
      </div>
      <ul className="divide-y divide-subtle rounded-panel border border-default bg-surface">
        {periods.map((period) => <HistoryRow key={period.id} period={period} />)}
      </ul>
      {footer}
    </section>
  );
}

/** History — payroll Finance has no action on: with HR, or closed. */
export default function PayrollHistory() {
  const { periods, loading, closed: closedPages, setClosedPage } = useOutletContext();
  if (loading) return <SkeletonRows rows={4} columns={3} label="Loading payroll history" />;

  const withHr = periods.filter(isWithHr);
  const closed = periods.filter((row) => ["settled", "cancelled"].includes(periodStatus(row)));

  if (!withHr.length && !closed.length) {
    return (
      <div className="rounded-panel border border-default bg-surface">
        <EmptyState title="No payroll history yet." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HistoryGroup title="With HR" description="Nothing for Finance to do until HR sends or releases it." periods={withHr} />
      <HistoryGroup
        title="Closed"
        description={closedPages?.total > closed.length ? `${closedPages.total} closed payroll periods, newest first, ${closedPages.pageSize} per page.` : undefined}
        periods={closed}
        footer={closedPages && setClosedPage ? (
          <Pagination
            page={closedPages.page}
            totalPages={Math.max(Math.ceil(closedPages.total / closedPages.pageSize), 1)}
            onPageChange={setClosedPage}
            label="Closed payroll pages"
          />
        ) : null}
      />
    </div>
  );
}
